import React, {useEffect, useState} from 'react';
import {Link, useParams, useSearchParams} from "react-router-dom";
import {useApiReadTeacherLessonDetail} from "@/api/lesson";
import RenderIf from "@/components/render-if";
import {Alert, Avatar, Badge, Button, Divider, Spin} from "antd";
import {BookOpen, Calendar, Clock} from 'lucide-react';
import {formatDate, progressBetweenDates} from "@/lib/helper/date";
import {teacherGenderNames} from "@/pages/student/lessons/take/_section/LessonCard";
import {weekdayString} from "@/lib/helper/weekday";
import toFaNumber from "@/lib/utils/number";
import {URLS} from "@/lib/urls";
import TeacherLayout from "@/components/layout/teacher/teacherLayout";
import TeacherStatCard from "@/pages/teacher/lessons/[id]/_section/TeacherStateCard";
import {sprintf} from "@/lib/utils/sprintf";
import {convertToJalaliWithFormat} from "@/lib/utils/date";
import {useApiSearchTeacherHomeworksReports} from "@/api/homework";
import RenderLoading from "@/components/render-if-loading";
import PaginationRaw from "@/components/pagination/PaginationRaw";
import HomeworkListItemComponent from "@/pages/teacher/homeworks/_section/HomeworkListItemComponent";


function TeacherShowLessonPage(props) {


    const param = useParams();

    const [startDate, setStartDate] = useState()
    const [endDate, setEndDate] = useState()
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(15)
    const [search, setSearch] = useState()
    const [searchParams, setSearchParams] = useSearchParams();

    const {data, isPending, isSuccess} = useApiReadTeacherLessonDetail(param.id)

    const {data: homeworksReports, isPending: isPendingGetHomework} = useApiSearchTeacherHomeworksReports({
        search: search, page: page, perPage: perPage,
        lesson_id: param.id, status: 'pending',
    })

    useEffect(() => {
        if (data?.data?.data?.startdate) {
            setStartDate(data?.data?.data?.startdate)
        } else {
            if (data?.data?.data?.semester?.start) {
                setStartDate(data?.data?.data?.semester?.start)
            }
        }

        if (data?.data?.data?.semester?.end) {
            setEndDate(data?.data?.data?.semester?.end)
        }

    }, [data])


    return (
        <TeacherLayout pageTitle={'کلاس درس' + ' ' + (data?.data?.data?.code ?? ' ... ')}
                       backTo={URLS.teacher.lessons.dashboard}>
            <RenderIf condition={isPending}>
                <Spin spinning={true}/>
            </RenderIf>

            <RenderIf condition={isSuccess}>

                <div className="container mx-auto  pt-2 max-w-7xl">

                    <Badge.Ribbon text={' کد درس: ' + toFaNumber(data?.data?.data?.code)}></Badge.Ribbon>
                    <div className=" rounded-2xl shadow-lg p-4 pt-8 mb-8 border dark:border-gray-600 border-gray-100">
                        <div className="flex flex-col lg:flex-row gap-8">
                            <div className="flex-1">
                                <div className="flex items-start gap-4 mb-6">
                                    <div className="hidden md:flex p-3 bg-blue-100 rounded-xl">
                                        <BookOpen className="w-8 h-8 text-blue-600"/>
                                    </div>
                                    <div className="flex-1">
                                        <h1 className="text-3xl font-bold   mb-2">{data?.data?.data?.topic?.title}

                                        </h1>
                                        <p className="  text-lg mb-4">{data?.data?.data?.title}</p>

                                    </div>
                                </div>

                                {/* Progress Bar */}
                                <div className="mb-6">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm font-medium  ">پیشرفت دوره</span>
                                        <span
                                            className="text-sm  ">{progressBetweenDates(startDate, endDate)?.percentElapsed}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-3">
                                        <div
                                            className="bg-gradient-to-r from-blue-500 to-emerald-500 h-3 rounded-full transition-all duration-500"
                                            style={{width: `${progressBetweenDates(startDate, endDate)?.percentElapsed}%`}}
                                        ></div>
                                    </div>
                                    <div className="flex justify-between text-xs   mt-1">
                                        <span>شروع: {formatDate(startDate)}</span>
                                        <span>پایان: {formatDate(endDate)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Instructor Info */}
                            <div className="lg:w-80">
                                <div className="bg-gradient-to-br from-blue-50 to-emerald-50 dark:from-blue-950/40 dark:to-emerald-950/30 rounded-xl p-2">
                                    <div className="text-center">
                                        <Avatar
                                            src={data?.data?.data?.teacher_detail?.avatar}
                                            alt={data?.data?.data?.teacher_detail?.firstname}
                                            className="w-20 h-20 rounded-full mx-auto mb-4 border-4 border-white shadow-md"
                                        />
                                        <h3 className="text-lg font-semibold   mb-1">
                                            {teacherGenderNames[data?.data?.data?.teacher_detail?.gender]} {' '}
                                            {data?.data?.data?.teacher_detail?.firstname}
                                            {' '}
                                            {data?.data?.data?.teacher_detail?.lastname}
                                        </h3>


                                        <div className="space-y-2 text-sm">
                                            <div className="flex items-center justify-center gap-2  ">

                                                {(data?.data?.data?.times_detail && data?.data?.data?.times_detail.length > 0) &&
                                                    <div className="flex items-center  ">
                                                        <Clock className="w-4 h-4 ml-2 text-green-500"/>
                                                        <span className="text-sm">
                                                            {data?.data?.data?.times_detail?.map((t, index) => (
                                                                <>
                                                                    <span className={'flex gap-1'}>
                                                                        <span
                                                                            className={'font-bold'}>
                                                                            <RenderIf condition={t.weekday}>
                                                                                {weekdayString(t.weekday)}
                                                                            </RenderIf>
                                                                            <RenderIf condition={t.date}>
                                                                                تاریخ
                                                                                {' '}
                                                                                {convertToJalaliWithFormat(t.date)}
                                                                            </RenderIf>
                                                                        </span>
                                                                        <RenderIf condition={t?.classroom?.title}>
                                                                            <span>{t.classroom.title}</span>
                                                                        </RenderIf>
                                                                        <span>{toFaNumber(t.start)} - {toFaNumber(t.end)}</span>
                                                                    </span>
                                                                </>
                                                            ))} </span>
                                                    </div>}
                                            </div>
                                            <RenderIf condition={data?.data?.data?.examdate}>
                                                <div className="flex items-center justify-center gap-2  ">
                                                    <Calendar className="w-4 h-4"/>
                                                    <span>آزمون: {data?.data?.data?.examdate}</span>
                                                </div>
                                            </RenderIf>

                                        </div>

                                        {/*<div className="mt-4 p-3  rounded-lg border">*/}
                                        {/*    <p className="text-xs   mb-1">جلسه بعدی</p>*/}
                                        {/*    <p className="text-sm font-medium  ">سسس</p>*/}
                                        {/*</div>*/}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={' p-2 rounded-xl shadow-md p-2'}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ">
                            <TeacherStatCard label="لیست اعضاء کلاس"
                                             value={data?.data?.data?.takenunits_count}
                                             sub="نفر در این کلاس"
                                             color="blue"
                                             linkTitle={'نمایش لیست اعضاء کلاس'}
                                             link={sprintf(URLS.teacher.lessons.students, {id: param.id})}/>

                            <TeacherStatCard label="ثبت نمرات"
                                             value={data?.data?.data?.takenunits_avg_score ? Math.round(data?.data?.data?.takenunits_avg_score) : '--'}
                                             sub={data?.data?.data?.takenunits_avg_score ? 'میانگین نمرات' : <>&nbsp;</>}
                                             color="emerald"
                                             linkTitle={'ثبت نمرات'}
                                             link={sprintf(URLS.teacher.lessons.score, {id: param.id})}/>

                            <TeacherStatCard label="تعداد کل غیبت‌ها"
                                             value={data?.data?.data?.total_absences_count}
                                             sub="نفر جلسه"
                                             color="amber"
                                             linkTitle={'ثبت حضور و غیاب'}
                                             link={sprintf(URLS.teacher.lessons.attendance, {id: param.id})}/>

                            <TeacherStatCard label="ثبت تکالیف"
                                             value={data?.data?.data?.homework_reports_count}
                                             sub={`${data?.data?.data?.homework_reports_count} تکلیف پاسخ داده شده از مجموع ${data?.data?.data?.homeworks_count} تکلیف ثبت شده `}
                                             color="red"
                                             linkTitle={'ثبت و مدیریت تکالیف'}
                                             link={sprintf(URLS.teacher.lessons.homeworks, {id: param.id})}/>
                        </div>


                        <Alert type="info"
                               className={'my-4'}
                               showIcon={true}

                               description={<>
                                   <div className={'flex justify-between flex-wrap'}>
                                       <div>
                                           در صورتی که نیاز به نسخه چاپی لیست کلاسی دارید از طریق این لینک می‌توانید
                                           لیست افراد کلاس خود را چاپ کنید
                                       </div>
                                       <div>
                                           <Link to={sprintf(URLS.teacher.lessons.print, {id: param.id})}>
                                               <Button color={'primary'} variant={'solid'}>

                                                   چاپ لیست کلاسی

                                               </Button>
                                           </Link>
                                       </div>
                                   </div>


                               </>}
                        ></Alert>
                    </div>

                    <RenderLoading loading={isPendingGetHomework} skeleton={true}>
                        <RenderIf condition={homeworksReports?.data?.data?.length > 0}>

                            <div className={'mt-4  p-2 rounded-xl shadow-md p-2'}>
                                <div>تکالیف ارسال شده و در انتظار بررسی شما</div>
                                <Divider type={'horizontal'}/>

                                {homeworksReports?.data?.data.map((homeworkReport, i) => (
                                    <HomeworkListItemComponent homeworkReport={homeworkReport}
                                                               key={'homework_' + homeworkReport?.id + '_' + i}/>
                                ))}
                                <PaginationRaw data={homeworksReports?.data} setPage={setPage} setPerPage={setPerPage}/>
                            </div>


                        </RenderIf>
                    </RenderLoading>

                </div>
            </RenderIf>


        </TeacherLayout>
    );

}

export default TeacherShowLessonPage;